import React from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckSquare, Square, X } from "lucide-react";

/**
 * Reusable sticky toolbar shown when at least one row is selected.
 * Also exports a `SelectAllHeader` and `RowCheckbox` for consistent table integration.
 *
 * Usage:
 *   const bulk = useBulkSelection(items.map(x=>x.id));
 *   <SelectAllHeader bulk={bulk} />
 *   <RowCheckbox bulk={bulk} id={row.id} />
 *   <BulkSelectionBar bulk={bulk} label="صنف">
 *     <Button onClick={() => onBulkDelete(bulk.selectedIds)}>حذف المحدد</Button>
 *   </BulkSelectionBar>
 */

export function useBulkSelection(allIds) {
  const [selected, setSelected] = React.useState(() => new Set());
  const idsRef = React.useRef(allIds);
  React.useEffect(() => { idsRef.current = allIds; }, [allIds]);

  const clear = React.useCallback(() => setSelected(new Set()), []);
  const toggle = React.useCallback((id) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }, []);
  const toggleAll = React.useCallback(() => {
    const current = idsRef.current || [];
    setSelected((prev) => (prev.size === current.length && current.length > 0)
      ? new Set()
      : new Set(current));
  }, []);
  const isSelected = React.useCallback((id) => selected.has(id), [selected]);

  const count = selected.size;
  const allCount = (allIds || []).length;
  const allSelected = count > 0 && count === allCount;
  const someSelected = count > 0 && count < allCount;

  return {
    selected,
    selectedIds: Array.from(selected),
    count,
    allCount,
    allSelected,
    someSelected,
    isSelected,
    toggle,
    toggleAll,
    clear,
    setSelected,
  };
}

export function SelectAllHeader({ bulk, className = "" }) {
  return (
    <div className={`inline-flex items-center ${className}`} title={bulk.allSelected ? "إلغاء تحديد الكل" : "تحديد الكل"}>
      <Checkbox
        checked={bulk.allSelected}
        onCheckedChange={bulk.toggleAll}
        aria-label="تحديد الكل"
        data-testid="bulk-select-all"
        className={bulk.someSelected ? "data-[state=unchecked]:bg-primary/30" : ""}
      />
    </div>
  );
}

export function RowCheckbox({ bulk, id, className = "" }) {
  return (
    <div className={`inline-flex items-center ${className}`} onClick={(e) => e.stopPropagation()}>
      <Checkbox
        checked={bulk.isSelected(id)}
        onCheckedChange={() => bulk.toggle(id)}
        aria-label="تحديد الصف"
        data-testid={`bulk-row-${id}`}
      />
    </div>
  );
}

export default function BulkSelectionBar({ bulk, label = "عنصر", children }) {
  if (bulk.count === 0) return null;
  return (
    <div
      className="sticky top-2 z-20 flex items-center flex-wrap gap-2 border border-primary/30 bg-primary/10 backdrop-blur px-3 py-2 rounded-lg shadow-sm animate-in fade-in slide-in-from-top-2"
      data-testid="bulk-selection-bar"
    >
      <div className="flex items-center gap-2 me-2">
        <CheckSquare className="w-4 h-4 text-primary" />
        <span className="text-sm font-semibold text-primary">
          محدد {bulk.count.toLocaleString("ar-EG")} {label}
          {bulk.allCount > 0 && <span className="text-xs text-muted-foreground"> من {bulk.allCount.toLocaleString("ar-EG")}</span>}
        </span>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">{children}</div>
      <div className="flex-1" />
      <Button variant="ghost" size="sm" onClick={bulk.clear} className="text-muted-foreground" data-testid="bulk-clear">
        <X className="w-4 h-4 me-1" /> إلغاء
      </Button>
    </div>
  );
}
